/* One company, with its roles — not four screens and four duplicates.
 *
 * ERPNext keeps a customer, a supplier, a contact and an address as four
 * unrelated records. So the same firm gets entered twice when it both buys
 * from you and sells to you, and the phone number somebody updated is on the
 * other copy.
 *
 * This answers what people actually ask: here is Karur Steel Works — what
 * are they to us, who do we speak to, and where are they? Nothing is merged;
 * the records stay as ERPNext keeps them and this reads across them.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useErp } from '@jackdesk/erp-kit'
import type { ContactAbout, ContactList, ErpError } from '@jackdesk/erp-kit'
import { EmptyByFilter, Failed, Loading } from '@jackdesk/shell'

export function Contacts() {
  const erp = useErp()
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const [data, setData] = useState<ContactList | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [open, setOpen] = useState<{ doctype: string; id: string } | null>(null)

  const load = useCallback(() => {
    setFailed(null)
    erp.contacts(text).then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp, text])

  useEffect(() => {
    const timer = window.setTimeout(load, text ? 220 : 0)
    return () => window.clearTimeout(timer)
  }, [load, text])

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 940 }}>
      <div>
        <h1>People and companies</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          Everyone you deal with, once each. A firm that both buys from you and sells to
          you is one row here with two roles, rather than two records that drift apart.
        </p>
      </div>

      <div className="row" style={{ gap: 10 }}>
        <input className="input" style={{ maxWidth: 320 }} value={text}
               placeholder="A name…" aria-label="Find a person or company"
               onChange={(e) => setText(e.target.value)} />
        <span className="grow" />
        {data ? (
          <span className="small muted">
            {data.count} names{data.more_than_one_role
              ? `, ${data.more_than_one_role} of them more than one thing to you` : ''}
          </span>
        ) : null}
      </div>

      {failed ? <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
        : !data ? <Loading rows={6} label="Looking" />
        : !data.people.length ? <EmptyByFilter onClear={() => setText('')} />
        : (
          <div className="card">
            <table className="tbl">
              <thead><tr><th>Name</th><th>What they are to you</th><th></th></tr></thead>
              <tbody>
                {data.people.map((p) => (
                  <tr key={p.name} style={{ cursor: 'default' }}>
                    <td>{p.name}</td>
                    <td>
                      {p.roles.map((r) => (
                        <button key={r.doctype + r.id} className="chip"
                                style={{ marginRight: 4, cursor: 'pointer', border: 0 }}
                                onClick={() => navigate(`/doc/${encodeURIComponent(r.doctype)}/${encodeURIComponent(r.id)}`)}>
                          {r.what}
                        </button>
                      ))}
                    </td>
                    <td>
                      {p.roles[0] ? (
                        <button className="btn btn-sm" onClick={() => setOpen(
                          open?.id === p.roles[0]!.id ? null
                            : { doctype: p.roles[0]!.doctype, id: p.roles[0]!.id })}>
                          {open?.id === p.roles[0]!.id ? 'Close' : 'Who and where'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {open ? <WhoAndWhere doctype={open.doctype} id={open.id} /> : null}
    </div>
  )
}

function WhoAndWhere({ doctype, id }: { doctype: string; id: string }) {
  const erp = useErp()
  const [about, setAbout] = useState<ContactAbout | null>(null)
  useEffect(() => {
    let cancelled = false
    setAbout(null)
    erp.contactAbout(doctype, id)
      .then((r) => { if (!cancelled) setAbout(r) })
      .catch(() => { if (!cancelled) setAbout(null) })
    return () => { cancelled = true }
  }, [erp, doctype, id])

  if (!about) return <div className="card card-pad"><Loading rows={3} label="Reading" /></div>

  return (
    <section className="card card-pad stack" style={{ gap: 12 }}>
      <div>
        <strong>{about.label}</strong>
        {about.also.length ? (
          <p className="small muted" style={{ margin: '2px 0 0' }}>
            Also: {about.also.map((a) => a.what).join(', ')}
          </p>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <div>
          <strong className="small">Who we speak to</strong>
          {about.contacts.length ? (
            <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
              {about.contacts.map((c) => (
                <li key={c.name} className="small">
                  {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.name}
                  {c.designation ? <span className="muted"> · {c.designation}</span> : null}
                  {c.mobile_no ? <span className="muted"> · {c.mobile_no}</span> : null}
                  {c.email_id ? <span className="muted"> · {c.email_id}</span> : null}
                </li>
              ))}
            </ul>
          ) : <p className="small muted" style={{ margin: '4px 0 0' }}>Nobody recorded yet.</p>}
        </div>
        <div>
          <strong className="small">Where they are</strong>
          {about.addresses.length ? (
            <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
              {about.addresses.map((a) => (
                <li key={a.name} className="small">
                  {[a.address_line1, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
                  {a.address_type ? <span className="muted"> · {a.address_type}</span> : null}
                </li>
              ))}
            </ul>
          ) : <p className="small muted" style={{ margin: '4px 0 0' }}>No address yet.</p>}
        </div>
      </div>
    </section>
  )
}
